import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Trash2, Shield, Briefcase, HardHat } from 'lucide-react';
import { apiService } from '@/lib/api';
import LenderShell, { LENDER_BLUE, LENDER_NAVY } from '@/components/lender/LenderShell';
import { getLenderActor, roleLabel } from '@/lib/lenderDemo';

const ROLE_ICON = { bank_admin: Shield, loan_officer: Briefcase, construction_dept: HardHat };

export default function LenderBankUsers() {
  const actor = getLenderActor();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', role: 'loan_officer', email: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const isAdmin = actor?.role === 'bank_admin';

  const load = useCallback(() => {
    if (!actor?.id || !isAdmin) return;
    apiService.lenderBankUsers(actor.id).then(({ data }) => setUsers(data.users || [])).catch(() => {});
  }, [actor?.id, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    setBusy(true);
    try {
      await apiService.lenderAddBankUser(actor.id, form);
      setForm({ name: '', role: 'loan_officer', email: '' });
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not add user');
    } finally { setBusy(false); }
  };

  const remove = async (uid) => {
    await apiService.lenderRemoveBankUser(actor.id, uid);
    load();
  };

  if (!isAdmin) {
    return (
      <LenderShell active="loans">
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <Shield className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <h2 className="font-semibold" style={{ color: LENDER_NAVY }}>Bank admins only</h2>
          <p className="text-slate-500 text-sm mt-1">Managing bank users is limited to the bank admin role.</p>
          <button onClick={() => navigate('/lender/loans')} className="mt-4 text-sm font-semibold" style={{ color: LENDER_BLUE }}>Back to loans</button>
        </div>
      </LenderShell>
    );
  }

  return (
    <LenderShell active="bankusers">
      <h1 className="text-2xl font-bold" style={{ color: LENDER_NAVY }}>Bank users</h1>
      <p className="text-slate-500 text-sm mb-5">Add loan officers and construction-department users for {actor.bank_name}. Loan officers only see loans assigned to them.</p>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Loans</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const Icon = ROLE_ICON[u.role] || Briefcase;
                return (
                  <tr key={u.id} data-testid={`bank-user-row-${u.id}`} className="border-b border-slate-50 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold" style={{ color: LENDER_NAVY }}>{u.name}</div>
                      {u.email && <div className="text-xs text-slate-400">{u.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-slate-600"><Icon className="w-4 h-4 text-slate-400" />{roleLabel(u.role)}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.role === 'loan_officer' ? u.loan_count : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      {u.id !== actor.id && (
                        <button onClick={() => remove(u.id)} data-testid={`bank-user-remove-${u.id}`} className="text-slate-400 hover:text-red-500" title="Remove user">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <form onSubmit={add} className="bg-white border border-slate-200 rounded-xl p-5 h-fit">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: LENDER_NAVY }}><UserPlus className="w-4 h-4" /> Add a user</h3>
          <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
          <input data-testid="bank-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full mb-3 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Full name" />
          <label className="block text-xs font-medium text-slate-500 mb-1">Role</label>
          <select data-testid="bank-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mb-3 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white">
            <option value="loan_officer">Loan officer</option>
            <option value="construction_dept">Construction dept.</option>
            <option value="bank_admin">Bank admin</option>
          </select>
          <label className="block text-xs font-medium text-slate-500 mb-1">Email (optional)</label>
          <input data-testid="bank-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mb-4 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="name@bank.com" />
          {error && <p className="text-xs text-red-500 mb-3" data-testid="bank-user-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="bank-user-add" className="w-full py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-60" style={{ background: LENDER_BLUE }}>
            {busy ? 'Adding…' : 'Add user'}
          </button>
        </form>
      </div>
    </LenderShell>
  );
}
